import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    Plus, Search, Filter, MoreVertical, 
    User, Phone, Mail, MapPin, 
    Calendar, ClipboardList, CheckCircle2,
    X, Edit3, Trash2, Loader2, AlertCircle,
    ChevronDown, ChevronUp, ExternalLink,
    FileText, MessageSquare, History, Send
} from 'lucide-react';

const API_BASE = (import.meta.env.VITE_APP_API_URL && import.meta.env.VITE_APP_API_URL !== "undefined") ? import.meta.env.VITE_APP_API_URL : "https://apiessentialcarehomehealth.saasbizhub.com/api/v1";

export default function CrmLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    
    // Selection
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadDetails, setLeadDetails] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [referralSources, setReferralSources] = useState([]);

    const fetchLeads = async (page = 1) => {
        const token = localStorage.getItem('admin_token');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/admin/crm/leads`, {
                params: { page, search, status: statusFilter },
                headers: { Authorization: `Bearer ${token}` }
            });
            setLeads(response.data.data);
            setPagination({
                current_page: response.data.current_page,
                last_page: response.data.last_page
            });
        } catch (err) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadDetails = async (id) => {
        const token = localStorage.getItem('admin_token');
        try {
            const response = await axios.get(`${API_BASE}/admin/crm/leads/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLeadDetails(response.data);
            setIsDetailOpen(true);
        } catch (err) {
            toast.error('Failed to load lead details');
        }
    };

    const fetchSources = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            const response = await axios.get(`${API_BASE}/admin/crm/referral-sources`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReferralSources(response.data);
        } catch (err) {
            console.error('Failed to load referral sources');
        }
    };

    useEffect(() => {
        fetchLeads();
        fetchSources();
    }, [search, statusFilter, pagination.current_page]);

    const handleCreateLead = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        const token = localStorage.getItem('admin_token');

        try {
            setIsSubmitting(true);
            await axios.post(`${API_BASE}/admin/crm/leads`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Lead created successfully');
            setIsCreateModalOpen(false);
            fetchLeads();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create lead');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateLead = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        const token = localStorage.getItem('admin_token');

        try {
            setIsSubmitting(true);
            await axios.put(`${API_BASE}/admin/crm/leads/${selectedLead.id}`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Lead updated successfully');
            setIsEditModalOpen(false);
            fetchLeads();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update lead');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        const token = localStorage.getItem('admin_token');
        try {
            await axios.patch(`${API_BASE}/admin/crm/leads/${id}/status`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Status updated');
            if (leadDetails?.id === id) {
                const updatedResponse = await axios.get(`${API_BASE}/admin/crm/leads/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setLeadDetails(updatedResponse.data);
            }
            fetchLeads(pagination.current_page);
        } catch (err) {
            toast.error('Failed to update status');
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        const token = localStorage.getItem('admin_token');
        try {
            await axios.post(`${API_BASE}/admin/crm/leads/${leadDetails.id}/notes`, { content: newNote }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewNote('');
            // Refresh details to show new note
            const updatedResponse = await axios.get(`${API_BASE}/admin/crm/leads/${leadDetails.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLeadDetails(updatedResponse.data);
            toast.success('Note added');
        } catch (err) {
            toast.error('Failed to add note');
        }
    };

    const handleDeleteLead = async (id) => {
        if (!window.confirm('Are you sure you want to delete this lead?')) return;
        const token = localStorage.getItem('admin_token');
        try {
            await axios.delete(`${API_BASE}/admin/crm/leads/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Lead deleted successfully');
            fetchLeads();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete lead');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'New': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'Contacted': return 'bg-orange-100 text-orange-700 border-orange-200';
            case 'Qualified': return 'bg-teal-100 text-teal-700 border-teal-200';
            case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'Converted': return 'bg-green-100 text-green-700 border-green-200';
            case 'Lost': return 'bg-red-100 text-red-700 border-red-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <Toaster position="top-right" />
            
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Lead Management</h1>
                    <p className="text-gray-500 mt-1">Track and nurture your potential patients.</p>
                </div>
                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl shadow-lg shadow-teal-600/20 transition-all active:scale-95"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Lead
                </button>
            </header>

            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input 
                        type="text" 
                        placeholder="Search by name, phone, email..."
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500 transition-all"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <select 
                        className="px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500 transition-all text-gray-600 font-medium"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Qualified">Qualified</option>
                        <option value="Pending">Pending</option>
                        <option value="Converted">Converted</option>
                        <option value="Lost">Lost</option>
                    </select>
                </div>
            </div>

            <div className="overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50/50 border-b border-gray-100">
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lead Info</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Service/City</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {loading ? (
                            Array(5).fill(0).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan="4" className="px-6 py-8 h-20 bg-gray-50/30"></td>
                                </tr>
                            ))
                        ) : leads.length > 0 ? (
                            leads.map((lead) => (
                                <motion.tr 
                                    layout
                                    key={lead.id} 
                                    className="hover:bg-gray-50/80 transition-colors group cursor-pointer"
                                    onClick={() => fetchLeadDetails(lead.id)}
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 font-bold border border-teal-100">
                                                {lead.name.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 group-hover:text-teal-700 transition-colors">{lead.name}</div>
                                                <div className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                                                    <Phone className="w-3 h-3" /> {lead.phone}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-700">{lead.service_requested}</div>
                                        <div className="text-xs text-gray-500 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {lead.city}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(lead.status)}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getStatusColor(lead.status).replace('bg-', 'bg-opacity-100 bg-')}`}></span>
                                            {lead.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => { setSelectedLead(lead); setIsEditModalOpen(true); }}
                                                className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteLead(lead.id)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center">
                                        <ClipboardList className="w-12 h-12 text-gray-200 mb-2" />
                                        <p>No leads found matching your search.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Detail Slide-over / Modal */}
            <AnimatePresence>
                {isDetailOpen && leadDetails && (
                    <div className="fixed inset-0 z-50 flex justify-end">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsDetailOpen(false)} className="absolute inset-0 bg-gray-900/40 backdrop-blur-xs" />
                        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="w-full max-w-xl bg-white shadow-2xl relative flex flex-col h-full" >
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">{leadDetails.name}</h2>
                                    <p className="text-sm text-gray-500">Lead Details & History</p>
                                </div>
                                <button onClick={() => setIsDetailOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X className="w-6 h-6" /></button>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6 space-y-8">
                                {/* Info Cards */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="text-xs text-gray-400 uppercase font-bold mb-1">Status</div>
                                        <select 
                                            value={leadDetails.status} 
                                            onChange={(e) => handleUpdateStatus(leadDetails.id, e.target.value)}
                                            className="w-full bg-transparent font-bold text-teal-700 focus:outline-none"
                                        >
                                            {['New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'].map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="text-xs text-gray-400 uppercase font-bold mb-1">Service</div>
                                        <div className="font-bold text-gray-900">{leadDetails.service_requested}</div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <section>
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Contact Information</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                                            <Phone className="w-5 h-5 text-teal-600" />
                                            <span className="text-gray-700">{leadDetails.phone}</span>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                                            <Mail className="w-5 h-5 text-teal-600" />
                                            <span className="text-gray-700">{leadDetails.email}</span>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                                            <MapPin className="w-5 h-5 text-teal-600" />
                                            <span className="text-gray-700">{leadDetails.city}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Notes Section */}
                                <section className="space-y-4">
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4" /> Notes & Activity
                                    </h3>
                                    <form onSubmit={handleAddNote} className="relative">
                                        <textarea 
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            placeholder="Write a note..."
                                            className="w-full p-4 pr-12 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all resize-none"
                                            rows="2"
                                        />
                                        <button 
                                            type="submit"
                                            className="absolute right-3 bottom-3 p-2 bg-teal-600 text-white rounded-xl shadow-lg hover:bg-teal-700 transition-all"
                                        >
                                            <Send className="w-4 h-4" />
                                        </button>
                                    </form>
                                    <div className="space-y-4">
                                        {leadDetails.lead_notes?.map(note => (
                                            <div key={note.id} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                                <div className="flex justify-between items-start mb-2">
                                                    <span className="text-xs font-bold text-teal-600">{note.user?.name}</span>
                                                    <span className="text-[10px] text-gray-400">{new Date(note.created_at).toLocaleString()}</span>
                                                </div>
                                                <p className="text-gray-700 text-sm">{note.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* Tasks Section */}
                                <section className="space-y-4">
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4" /> Assigned Tasks
                                    </h3>
                                    <div className="space-y-2">
                                        {leadDetails.tasks?.map(task => (
                                            <div key={task.id} className={`p-3 rounded-xl border flex items-center justify-between ${task.is_completed ? 'bg-gray-50 border-gray-100 line-through text-gray-400' : 'bg-white border-teal-100 text-gray-700'}`}>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium">{task.title}</span>
                                                </div>
                                                <span className="text-[10px]">{new Date(task.due_date).toLocaleDateString()}</span>
                                            </div>
                                        ))}
                                        {leadDetails.tasks?.length === 0 && <p className="text-sm text-gray-400 italic">No tasks assigned yet.</p>}
                                    </div>
                                </section>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Pagination & Create/Edit Modals remain as before */}
            {/* [Omitted for brevity, but I'll make sure they are included in the actual file write] */}
            
            {/* Create Modal */}
            <AnimatePresence>
                {isCreateModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsCreateModalOpen(false)} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative" >
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900">Add New Lead</h2>
                                <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors"><X className="w-6 h-6" /></button>
                            </div>
                            <form onSubmit={handleCreateLead} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Full Name *</label>
                                        <input name="name" required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="John Doe" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Phone Number *</label>
                                        <input name="phone" required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="+1 (555) 000-0000" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Email Address *</label>
                                        <input name="email" type="email" required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="john@example.com" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">City *</label>
                                        <input name="city" required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="Los Angeles" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Service Requested *</label>
                                        <select name="service_requested" required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                            <option value="Home Health">Home Health</option>
                                            <option value="Hospice">Hospice</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Referral Source</label>
                                        <select name="referral_source_id" className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                            <option value="">Unknown / Walk-in</option>
                                            {referralSources.map(source => (
                                                <option key={source.id} value={source.id}>{source.facility_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Notes</label>
                                    <textarea name="notes" rows="3" className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" placeholder="Additional information..."></textarea>
                                </div>
                                <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                                    <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-6 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl">Cancel</button>
                                    <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 bg-teal-600 text-white font-medium rounded-xl shadow-lg flex items-center gap-2">{isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Lead'}</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Edit Modal */}
            <AnimatePresence>
                {isEditModalOpen && selectedLead && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsEditModalOpen(false)} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative" >
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900">Edit Lead</h2>
                                <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors"><X className="w-6 h-6" /></button>
                            </div>
                            <form onSubmit={handleUpdateLead} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Full Name *</label>
                                        <input name="name" defaultValue={selectedLead.name} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Phone Number *</label>
                                        <input name="phone" defaultValue={selectedLead.phone} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Email Address *</label>
                                        <input name="email" type="email" defaultValue={selectedLead.email} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">City *</label>
                                        <input name="city" defaultValue={selectedLead.city} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Service Requested *</label>
                                        <select name="service_requested" defaultValue={selectedLead.service_requested} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                            <option value="Home Health">Home Health</option>
                                            <option value="Hospice">Hospice</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Status *</label>
                                        <select name="status" defaultValue={selectedLead.status} required className="w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500">
                                            {['New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'].map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                                    <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-6 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl">Cancel</button>
                                    <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 bg-teal-600 text-white font-medium rounded-xl shadow-lg flex items-center gap-2">{isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Update Lead'}</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
